import { joiResolver } from '@hookform/resolvers/joi';
import React from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@src/components/atoms/inputs/button';
import { TextLink } from '@src/components/atoms/text-link';
import { ContentWithLink } from '@src/components/common/formatting/content-with-link';
import Seo from '@src/components/common/seo/seo';
import { InputController } from '@src/components/input-controllers/input-controller';
import { Form } from '@src/components/molecules/form';
import { schemaMessages } from '@src/constants/schema-messages';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { AuthLayout } from '@src/features/auth/components/auth-layout';
import { useErrorMessages } from '@src/hooks/use-error-messages';

import { loginSchema } from './schema';
import { Login, LoginFormProps } from './types';

const LoginForm = ({ isLoading, email: defaultEmail, onLogin, onForgotPassword }: LoginFormProps) => {
  const content = useCmsContent();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<Login>({
    defaultValues: {
      email: defaultEmail,
    },
    resolver: joiResolver(loginSchema, {
      messages: schemaMessages,
    }),
    criteriaMode: 'all',
  });

  const { email, password } = watch();
  const errorMessages = useErrorMessages<Login>(errors);

  const onSubmit = () => onLogin(email, password);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Seo title={content.loginFormHeading} />

      <AuthLayout.Header size="small">{content.loginFormHeading}</AuthLayout.Header>

      <InputController
        name="email"
        type="email"
        autoComplete="email"
        error={errorMessages.email}
        label={content.labelEmail}
        control={control}
      />
      <InputController
        error={errorMessages.password}
        label={content.labelPassword}
        control={control}
        name="password"
        type="password"
        autoComplete="current-password"
        autoFocus
      />

      <TextLink onClick={() => onForgotPassword(email)}>{content.forgotPasswordCta}</TextLink>

      <AuthLayout.Footer>
        <Button size="small" type="submit" isLoading={isLoading}>
          {content.continueButton}
        </Button>

        <ContentWithLink href="/auth" passHref shallow contentKey="signUpCta" />
      </AuthLayout.Footer>
    </Form>
  );
};
export default LoginForm;
